import { useCallback, useEffect, useState } from "react";

import { GrantsScreen } from "./screens/GrantsScreen";
import { Note } from "./components/Note";
import { NotAnAdminError, NotInTelegramError } from "./api/client";
import { RefundsScreen } from "./screens/RefundsScreen";
import { ScrapersScreen } from "./screens/ScrapersScreen";
import { StatsScreen } from "./screens/StatsScreen";
import { TicketScreen } from "./screens/TicketScreen";
import { TicketsScreen } from "./screens/TicketsScreen";
import { applyTheme } from "./telegram/theme";
import { watchInsets } from "./telegram/viewport";
import { announceReady } from "./telegram/sdk";
import { describeError } from "./api/errors";
import { getMe } from "./api/endpoints";
import { t } from "./i18n/strings";

type Tab = "tickets" | "refunds" | "grants" | "scrapers" | "stats";

/**
 * What the application is allowed to show.
 *
 * "outside" and "forbidden" are two different failures and two different screens: one means
 * the app was opened in a plain browser where telegram never signed the launch, the other
 * means the launch was fine and the person is simply not on the admin list. Telling them apart
 * saves the second one from looking for a bug that is not there.
 */
type Access = "loading" | "ready" | "outside" | "forbidden" | "error";

const TABS: {
  value: Tab;
  label: "tabs.tickets" | "tabs.refunds" | "tabs.grants" | "tabs.scrapers" | "tabs.stats";
}[] = [
  { value: "tickets", label: "tabs.tickets" },
  { value: "refunds", label: "tabs.refunds" },
  { value: "grants", label: "tabs.grants" },
  { value: "scrapers", label: "tabs.scrapers" },
  { value: "stats", label: "tabs.stats" },
];

export function App() {
  const [access, setAccess] = useState<Access>("loading");
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("tickets");
  const [ticketId, setTicketId] = useState<number | null>(null);
  const [paymentsUserId, setPaymentsUserId] = useState<number | undefined>(undefined);
  const [grantUserId, setGrantUserId] = useState<number | undefined>(undefined);
  const [openTickets, setOpenTickets] = useState(0);

  const check = useCallback(async () => {
    setAccess("loading");
    setError(null);
    try {
      const me = await getMe();
      setOpenTickets(me.open_tickets);
      setAccess("ready");
    } catch (failure) {
      if (failure instanceof NotInTelegramError) {
        setAccess("outside");
      } else if (failure instanceof NotAnAdminError) {
        setAccess("forbidden");
      } else {
        setError(describeError(failure));
        setAccess("error");
      }
    }
  }, []);

  useEffect(() => {
    applyTheme();
    announceReady();
    const stopWatchingInsets = watchInsets();
    void check();
    return stopWatchingInsets;
  }, [check]);

  const showPayments = useCallback((userId: number) => {
    setPaymentsUserId(userId);
    setTicketId(null);
    setTab("refunds");
  }, []);

  if (access === "loading") {
    return <main className="app app--message">{t("app.loading")}</main>;
  }

  if (access !== "ready") {
    return (
      <main className="app app--message">
        <Note kind={access === "error" ? "error" : "warning"}>
          {access === "outside" ? t("app.outside") : access === "forbidden" ? t("app.forbidden") : error}
        </Note>
        {access === "error" ? (
          <button type="button" className="button" onClick={() => void check()}>
            {t("app.retry")}
          </button>
        ) : null}
      </main>
    );
  }

  return (
    <main className="app">
      <header className="header">
        <h1 className="header__title">{t("app.title")}</h1>
      </header>

      <nav className="tabs">
        {TABS.map((one) => (
          <button
            key={one.value}
            type="button"
            className={`tabs__tab${tab === one.value ? " tabs__tab--active" : ""}`}
            onClick={() => {
              setTab(one.value);
              if (one.value !== "tickets") {
                setTicketId(null);
              }
              if (one.value !== "refunds") {
                setPaymentsUserId(undefined);
              }
              if (one.value !== "grants") {
                setGrantUserId(undefined);
              }
            }}
          >
            {t(one.label)}
            {one.value === "tickets" && openTickets > 0 ? <span className="tabs__badge">{openTickets}</span> : null}
          </button>
        ))}
      </nav>

      {/* Unlike the client app the screens are not kept mounted: the ticket list polls, the
          refund dialog holds a payment out of the queue, and a hidden screen doing either of
          those is a surprise nobody asked for. */}
      {tab === "tickets" ? (
        ticketId === null ? (
          <TicketsScreen onOpen={setTicketId} onOpenCount={setOpenTickets} />
        ) : (
          <TicketScreen ticketId={ticketId} onBack={() => setTicketId(null)} onShowPayments={showPayments} />
        )
      ) : null}

      {tab === "refunds" ? <RefundsScreen userId={paymentsUserId} /> : null}
      {tab === "grants" ? <GrantsScreen userId={grantUserId} /> : null}
      {tab === "scrapers" ? <ScrapersScreen /> : null}
      {tab === "stats" ? <StatsScreen /> : null}
    </main>
  );
}
